import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import type { ChangeEvent } from 'react';

type MyInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  label: string;
  type: string;
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onChange?: (value: string) => void;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  formatter?: (value: string) => string;
};
export const RHFControlledInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  label,
  type,
  className = '',
  readOnly = false,
  disabled = false,
  onChange,
  placeholder,
  formatter,
  control,
  errors,
}: MyInputProps<TFieldValues, TName>) => {
  const errorMessage =
    typeof errors[name]?.message === 'string' ? errors[name].message : '';
  const outputTransform = (
    e: ChangeEvent<HTMLInputElement, HTMLInputElement>,
  ) => {
    if (type === 'number' && !formatter) {
      const output = +e.target.value;
      return isNaN(output) ? 0 : output;
    }
    if (formatter) {
      return formatter(e.target.value);
    }
    return e.target.value;
  };

  const inputTransform = (value: any) => {
    if (type === 'number' && !formatter) {
      return isNaN(value) || !value ? '' : value.toString();
    }
    if (formatter) {
      return formatter(value);
    }
    return value;
  };
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className='mb-2 py-2'>
          <label
            htmlFor={name}
            className='flex justify-between text-xs font-bold uppercase'
          >
            {label}
          </label>
          <input
            {...field}
            name={name}
            id={name}
            readOnly={readOnly}
            disabled={disabled}
            placeholder={placeholder ?? label}
            onChange={(e) => {
              field.onChange(outputTransform(e));
              if (onChange) onChange(e.target.value);
            }}
            value={inputTransform(field.value)}
            type={type}
            className={`${className} g-gray-50 mt-1 block w-full  rounded-lg border border-gray-500 p-2.5
                       text-gray-900 focus:border-blue-500 focus:ring-blue-500
                       dark:border-0 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-gray-400
                         ${errorMessage ? 'border-rose-600 dark:border-rose-400' : ''}`}
          />

          {errorMessage && (
            <span className='text-xs text-rose-600 dark:text-rose-400'>
              {errorMessage}
            </span>
          )}
        </div>
      )}
    />
  );
};
